import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { AdminHeader, Card, Btn, Modal, Input, Select, Toggle, StatusBadge, Empty } from "@/components/admin/ui";
import { formatINR } from "@/lib/format";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Sparkles } from "lucide-react";

const EMPTY = { name: "", condition: "minSubtotal", threshold: 1000, type: "percentage", value: 10, active: true };
const CONDITION_LABEL = { minSubtotal: "Minimum cart subtotal", minQuantity: "Minimum item quantity" };

const discountText = (r) => (r.type === "percentage" ? `${r.value}% off` : `${formatINR(r.value)} off`);
const ruleSummary = (r) => (r.condition === "minQuantity"
  ? `Buy ${r.threshold} or more items`
  : `Spend ${formatINR(r.threshold)} or more`);

export default function AdminAutoDiscounts() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/auto-discounts").then(({ data }) => setItems(data.autoDiscounts || []));
  useEffect(() => { load(); }, []);

  const openNew = () => { setF(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (r) => { setF({ ...EMPTY, ...r }); setEditId(r.id); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    // Mirrors the backend rules for instant feedback — the server re-validates
    // and stays authoritative.
    const threshold = Number(f.threshold);
    const value = Number(f.value);
    if (!f.name.trim()) { toast.error("Rule name is required"); return; }
    if (!Number.isFinite(threshold) || threshold < 0) { toast.error("Threshold cannot be negative"); return; }
    if (f.condition === "minQuantity" && threshold < 1) { toast.error("Minimum quantity must be at least 1"); return; }
    if (!Number.isFinite(value) || value <= 0) { toast.error("Discount value must be greater than 0"); return; }
    if (f.type === "percentage" && value > 100) { toast.error("Percentage discount cannot be more than 100%"); return; }
    setSaving(true);
    try {
      const payload = { name: f.name.trim(), condition: f.condition, threshold, type: f.type, value, active: !!f.active };
      if (editId) await api.put(`/admin/auto-discounts/${editId}`, payload);
      else await api.post("/admin/auto-discounts", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  const toggle = async (r) => {
    try {
      await api.put(`/admin/auto-discounts/${r.id}/status`, { active: !r.active });
      toast.success(r.active ? "Rule disabled" : "Rule enabled");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this discount rule?")) return;
    try { await api.delete(`/admin/auto-discounts/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div>
      <AdminHeader title="Automatic Discounts" subtitle={`${items.length} rules`} action={<Btn variant="accent" onClick={openNew} data-testid="add-auto-discount-btn"><Plus className="h-4 w-4" /> Add Rule</Btn>} />

      <Card className="p-4 mb-5 flex items-start gap-3">
        <span className="h-9 w-9 rounded-lg bg-amber-100 text-amber-700 grid place-items-center shrink-0"><Sparkles className="h-5 w-5" /></span>
        <p className="text-sm text-slate-600">
          Applied automatically in the cart and at checkout — customers don't enter a code. When several rules qualify,
          {" "}<span className="font-semibold text-slate-900">only the single highest-value rule</span> is applied. Automatic
          discounts are never stacked with each other or with a coupon, and they are calculated on the product subtotal only.
        </p>
      </Card>

      <Card className="overflow-hidden">
        <table className="w-full text-sm text-left">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
            <tr><th className="p-3">Rule</th><th className="p-3">Applies when</th><th className="p-3">Discount</th><th className="p-3">Status</th><th className="p-3 text-right">Actions</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.map((r) => (
              <tr key={r.id} className="hover:bg-slate-50" data-testid={`auto-discount-${r.id}`}>
                <td className="p-3">
                  <p className="font-semibold text-slate-900">{r.name}</p>
                  <p className="text-[11px] text-slate-400">{CONDITION_LABEL[r.condition] || r.condition}</p>
                </td>
                <td className="p-3">{ruleSummary(r)}</td>
                <td className="p-3 font-mono">{discountText(r)}</td>
                <td className="p-3"><StatusBadge status={r.active ? "active" : "inactive"} /></td>
                <td className="p-3">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => toggle(r)} className="px-2 py-1 text-xs font-semibold text-slate-500 hover:text-amber-600" data-testid={`toggle-auto-discount-${r.id}`}>
                      {r.active ? "Disable" : "Enable"}
                    </button>
                    <button onClick={() => openEdit(r)} className="p-2 text-slate-500 hover:text-amber-600" data-testid={`edit-auto-discount-${r.id}`}><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => del(r.id)} className="p-2 text-slate-500 hover:text-red-600" data-testid={`delete-auto-discount-${r.id}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && <Empty text="No automatic discounts yet" />}
      </Card>

      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Edit Discount Rule" : "Add Discount Rule"}>
        <form onSubmit={save} className="space-y-4">
          <Input label="Rule name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. Spend ₹1,000 get 10% off" testid="ad-name" />
          <Select label="Discount condition" value={f.condition} onChange={(e) => setF({ ...f, condition: e.target.value })} testid="ad-condition">
            <option value="minSubtotal">Minimum cart subtotal (₹)</option>
            <option value="minQuantity">Minimum total product quantity</option>
          </Select>
          <Input label={f.condition === "minQuantity" ? "Minimum quantity" : "Minimum purchase amount (₹)"} type="number" min="0"
            value={f.threshold} onChange={(e) => setF({ ...f, threshold: e.target.value })} testid="ad-threshold" />
          <div className="grid grid-cols-2 gap-3">
            <Select label="Discount type" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} testid="ad-type">
              <option value="percentage">Percentage (%)</option>
              <option value="fixed">Fixed amount (₹)</option>
            </Select>
            <Input label={f.type === "percentage" ? "Discount value (%)" : "Discount value (₹)"} type="number" min="0"
              max={f.type === "percentage" ? 100 : undefined} value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })} testid="ad-value" />
          </div>
          <Toggle label="Enabled" checked={f.active} onChange={(v) => setF({ ...f, active: v })} testid="ad-active" />
          <p className="text-xs text-slate-500">
            The discount applies to the product subtotal only (never shipping, tax or handling) whenever the cart meets the condition.
          </p>
          <Btn variant="accent" type="submit" className="w-full" loading={saving} data-testid="save-auto-discount-btn">Save Rule</Btn>
        </form>
      </Modal>
    </div>
  );
}
